import clsx from 'clsx';
import { Link } from 'react-router-dom';
import { PageSection } from 'src/components/common/PageSection';
import { ActivePosEntityProvider } from 'src/contexts/ActivePosEntityContext';
import { CatalogDataContextProvider } from 'src/contexts/CatalogDataContext';
import { Content } from 'src/contexts/ContentContext';
import { DialogProvider } from 'src/contexts/DialogContext/DialogContext';
import { FilterQueryContextProvider } from 'src/contexts/FilterQueryContext';
import { PosSpinner } from 'src/core/POS/PosSpinner';
import { ToolbarButton } from 'src/core/POS/ToolbarButton';
import { shared } from 'src/core/themes';
import { Button, Card, Stack } from 'src/core/ui';
import { useMatchMedia } from 'src/hooks/useMatchMedia';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { SaleWithEvent } from 'src/tables/SalesTable/SalesTable.type';
import { RowWrapper } from 'src/tables/Table/Table.types';
import { ContentId } from 'src/utils/constants/contentId';
import { transformData } from 'src/utils/eventWithDataUtils';
import {
  ActionOutboxEntityType,
  Feature,
  SaleDetails,
  SaleQuery,
  UserSetting,
} from 'src/WebApiController';

import { DashboardSectionWrapper } from '../../Dashboard/DashboardSectionWrapper';
import * as styles from './RecentSalesSection.css';
import { RecentSalesTableV2 } from './RecentSalesTableV2';
import { RecentSalesTableV3 } from './RecentSalesTableV3';
import { RecentSalesTilesView } from './RecentSalesTilesView';
import { useRecentSalesSection } from './useRecentSalesSection';

export const RecentSalesSectionV2 = ({
  hasPadding = true,
}: {
  hasPadding?: boolean;
}) => {
  const {
    salesFilterQuery,
    newRows,
    activeTab,
    handleTabChange,
    flattenedData,
    isLoading,
    refetchUnableToProcessSales,
    getCatalogDataExpanded,
    getActivePosEntity,
  } = useRecentSalesSection();

  const isMobile = useMatchMedia('mobile');

  const hasRecentSalesTilesFeature = useUserHasFeature(
    Feature.RecentSalesTiles
  );

  // Tiles replace the table on mobile only; desktop/tablet keep the table
  const showTiles = hasRecentSalesTilesFeature && isMobile;

  const hasRecentSalesTableV3Feature = useUserHasFeature(
    Feature.RecentSalesTableV3
  );

  const onRowWrapper: RowWrapper<SaleWithEvent | null> = (
    rowData,
    tableRow
  ) => {
    const isNewRow = !!rowData?.sale.id && newRows.includes(rowData?.sale.id);

    return (
      <div className={clsx(styles.row, { [styles.isNewRow]: isNewRow })}>
        {tableRow}
      </div>
    );
  };

  return (
    <PageSection>
      <DashboardSectionWrapper fixed hasPadding={hasPadding}>
        <PageSection.Header showDivider={false}>
          <Stack direction="row" gap="m" alignItems="center">
            <span className={shared.typography.title6}>
              <Content id={ContentId.SalesFeed} />
            </span>
            <ToolbarButton
              isSelected={activeTab === 'recentSales'}
              onClick={() => handleTabChange('recentSales')}
            >
              <Content id={ContentId.RecentSales} />
            </ToolbarButton>
            <ToolbarButton
              isSelected={activeTab === 'unableToProcessSales'}
              onClick={() => handleTabChange('unableToProcessSales')}
            >
              <Content id={ContentId.UnableToProcess} />
            </ToolbarButton>
          </Stack>
        </PageSection.Header>
      </DashboardSectionWrapper>

      <DashboardSectionWrapper hasPadding={hasPadding}>
        <FilterQueryContextProvider<SaleQuery>
          initialQuery={salesFilterQuery}
          emptyQuery={salesFilterQuery}
          viewModeSettingId={UserSetting.SalePageViewMode}
        >
          <CatalogDataContextProvider<SaleQuery>
            entityType={ActionOutboxEntityType.Sale}
            queryKey="getCatalogForSales"
            getCatalogDataExpanded={getCatalogDataExpanded}
            transformEventData={transformData}
          >
            <ActivePosEntityProvider<SaleDetails>
              entityType={ActionOutboxEntityType.Sale}
              getActivePosEntity={getActivePosEntity}
              onSetActiveItemCompleteCallback={() =>
                refetchUnableToProcessSales()
              }
            >
              <DialogProvider>
                {showTiles ? (
                  !isLoading ? (
                    <RecentSalesTilesView
                      data={flattenedData}
                      newRows={newRows}
                    />
                  ) : (
                    <PosSpinner />
                  )
                ) : !hasRecentSalesTableV3Feature ? (
                  <Card className={styles.cardV2}>
                    {!isLoading ? (
                      <RecentSalesTableV2
                        data={flattenedData}
                        onRowWrapper={onRowWrapper}
                      />
                    ) : (
                      <PosSpinner />
                    )}
                  </Card>
                ) : !isLoading ? (
                  <RecentSalesTableV3
                    data={flattenedData}
                    onRowWrapper={onRowWrapper}
                  />
                ) : (
                  <PosSpinner />
                )}
              </DialogProvider>
            </ActivePosEntityProvider>
          </CatalogDataContextProvider>
        </FilterQueryContextProvider>
      </DashboardSectionWrapper>
      {!showTiles && !hasRecentSalesTableV3Feature && (
        <DashboardSectionWrapper fixed hasPadding={hasPadding}>
          <Stack className={styles.ctaContainer}>
            <Link className={styles.link} to="/sales">
              <Button variant="outline" className={styles.cta}>
                <Content id={ContentId.ViewAllSales} />
              </Button>
            </Link>
          </Stack>
        </DashboardSectionWrapper>
      )}
    </PageSection>
  );
};
